// Pure rules for the run-status banner on /roadmap: what the latest ledger row
// (roadmap_runs) means to the person who pressed the button.

import type { RoadmapRun, RoadmapRunFailedStep } from '../../services/roadmapReports';

export type RunTone = 'progress' | 'ok' | 'bad';

export interface RunStatusView {
  tone: RunTone;
  headline: string;
  detail: string;
  // Keep polling the ledger while true.
  active: boolean;
  canRetry: boolean;
}

// Ledger timestamps are naive UTC (no offset) — parse them as UTC.
export function parseLedgerTime(iso: string): number {
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
}

export function elapsedLabel(fromIso: string, nowMs: number): string {
  const secs = Math.max(0, Math.floor((nowMs - parseLedgerTime(fromIso)) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  const hours = Math.floor(mins / 60);
  // Past two days nobody wants "144h 30m": say days.
  return hours < 48 ? `${hours}h ${mins % 60}m` : `${Math.floor(hours / 24)} days`;
}

const FAILED_AT: Record<RoadmapRunFailedStep, string> = {
  lost: 'The run went silent mid-flight (the agent was restarted) and had no attempts left.',
  dispatch: 'The worker could not reach the agent.',
  agent: 'The agent hit an error while working.',
  not_published: 'The agent finished without publishing a valid report.',
  persist: 'The report was produced but could not be saved to the roadmap.',
};

// The skip guard's one-line reason on a skipped tick (roadmap_runs.detail.skip.reason).
export function skipReason(run: RoadmapRun): string | null {
  const skip = run.detail?.skip;
  if (skip && typeof skip === 'object' && 'reason' in skip) {
    const reason = (skip as { reason?: unknown }).reason;
    if (typeof reason === 'string' && reason.trim()) return reason;
  }
  return null;
}

// Capitalise and close a reason so it reads as a sentence in the banner.
function sentence(text: string): string {
  const t = text.trim();
  const capped = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(capped) ? capped : `${capped}.`;
}

export function describeRun(run: RoadmapRun, nowMs: number): RunStatusView {
  const who = run.trigger === 'schedule' ? 'the weekday schedule' : run.triggered_by_email ?? 'an admin';
  switch (run.state) {
    case 'queued':
      return {
        tone: 'progress',
        headline: `Roadmap run queued (${elapsedLabel(run.queued_at, nowMs)})`,
        detail:
          run.attempts > 0
            ? `Attempt ${run.attempts} was lost mid-flight; waiting for a worker to pick it up again.`
            : `Requested by ${who}. A worker picks it up within a minute.`,
        active: true,
        canRetry: false,
      };
    case 'running':
      return {
        tone: 'progress',
        headline: `Roadmap run in progress (${elapsedLabel(run.started_at ?? run.queued_at, nowMs)})`,
        detail:
          run.model === null
            ? 'Handed to the agent; waiting for it to start.'
            : `Merlin is working on ${run.model} with ${run.meeting_ids.length} new meeting(s)` +
              ` and ${run.gap_count ?? 0} gap(s) to ask about. Runs usually take 15 to 20 minutes.`,
        active: true,
        canRetry: false,
      };
    case 'published':
      return {
        tone: 'ok',
        headline: 'Last roadmap run published',
        detail: `Finished ${elapsedLabel(run.finished_at ?? run.queued_at, nowMs)} ago, requested by ${who}.`,
        active: false,
        canRetry: false,
      };
    case 'skipped':
      return {
        tone: 'ok',
        headline: 'This morning\'s scheduled run was skipped: nothing new to read',
        detail:
          `${sentence(skipReason(run) ?? 'Nothing new since the last report')} ` +
          `Checked ${elapsedLabel(run.finished_at ?? run.queued_at, nowMs)} ago; the roadmap is unchanged and no run was spent. ` +
          'Use "Run roadmap now" to run anyway.',
        active: false,
        canRetry: false,
      };
    case 'failed':
      return {
        tone: 'bad',
        headline: 'Last roadmap run failed',
        detail: `${run.failed_step ? FAILED_AT[run.failed_step] : 'The run failed.'} ${run.error ?? ''}`.trim(),
        active: false,
        canRetry: true,
      };
  }
}
